import {
  CloseOutlined,
  CreditScore,
  CreditScoreOutlined,
  EastOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  MailOutline,
  Person,
  TextSnippetOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import { isEmpty, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useAssociationUtils from "../../../../../../components/associations/hooks/useAssociationUtils";
import PreviewableLoanFileButton from "../../../../../../components/common/Loan/buttons/PreviewableLoanFileButton";
import Flx from "../../../../../../components/layout/Flx";
import Txt from "../../../../../../components/typography/Txt";
import { getFirstLetterOfString } from "../../../../../../utils/strings/getFirstLetterOfString";
import BorrowerRequiredDocumentsChecklist from "../common/BorrowerRequiredDocumentsChecklist";
import UserAvatar from "../../../../../../components/ui/UserAvatar";
import AssignLoanFilesButton from "../../../../../../components/common/Loan/buttons/AssignLoanFilesButton";
import SendBorrowerAppLinkButton from "../../../../../../components/common/Loan/buttons/SendBorrowerAppLinkButton";

const BorrowerSummaryCardItem = ({ borrower, activeBorrowerId }) => {
  const { id: loanId } = useParams();

  const currentBorrowerActive = useMemo(
    () => borrower?._id === activeBorrowerId,
    [borrower?._id, activeBorrowerId]
  );

  const navigate = useNavigate();

  const onBorrowerClick = useCallback(
    (id) => {
      if (id === activeBorrowerId) {
        navigate(`/loan/${loanId}/borrowers`);
        return;
      }
      navigate(`/loan/${loanId}/borrowers/${id}`);
    },
    [activeBorrowerId, navigate, loanId]
  );

  return (
    <Card
      key={borrower._id}
      sx={{
        p: 2.5,
        position: "relative",
        borderColor: currentBorrowerActive ? "#bbdefb" : "#ffffff",
        borderWidth: currentBorrowerActive ? 2 : 0,
        flexGrow: 1,
        // flexGrow: activeBorrowerId ? 0 : 1,
        flexBasis: activeBorrowerId ? "400px" : "400px",
        // flexBasis: activeBorrowerId ? "300px" : "400px",
        maxWidth: "900px",
        flexShrink: 1,
      }}
    >
      {currentBorrowerActive && (
        <IconButton
          sx={{ position: "absolute", top: 14, right: 14 }}
          onClick={() => onBorrowerClick(borrower?._id)}
        >
          <CloseOutlined />
        </IconButton>
      )}
      <Flx g={1} column key={borrower._id}>
        <Flx ac gap={1.2}>
          <UserAvatar
            size={36}
            label={`${borrower?.firstName} ${borrower?.lastName}`}
          />

          <Flx column>
            <Txt bold fontSize={"14px"}>
              {borrower?.firstName} {borrower?.lastName}
            </Txt>
            <Tooltip title={"Role"} arrow placement="right">
              <Box sx={{ width: "max-content", mt: "-3px" }}>
                <Txt sx={{ lineHeight: 1.2, fontStyle: "italic" }} secondary>
                  {borrower?.loanRole}
                </Txt>
              </Box>
            </Tooltip>
            <Flx wrap gap={2} sx={{ mt: 0.2 }}>
              <DisplayPreviewItem
                label={"Email Address"}
                value={borrower?.emailAddress || "Unknown"}
                icon={
                  <MailOutline sx={{ fontSize: "12px" }} className="thin" />
                }
              />
              {/* <DisplayPreviewItem
                label={"Phone Number"}
                value={formatPhone(borrower?.phone) || "Unknown"}
                icon={
                  <PhoneOutlined sx={{ fontSize: "12px" }} className="thin" />
                }
              />
              <DisplayPreviewItem
                label={"Date of Birth"}
                value={formatDate(borrower?.dateOfBirth) || "Unknown"}
                icon={
                  <CalendarMonth sx={{ fontSize: "12px" }} className="thin" />
                }
              /> */}
            </Flx>
            {/* {activeBorrowerId ? null : (
              <Flx wrap gap={2} sx={{ mt: 0.5 }}>
                <DisplayPreviewItem
                  label={"Email Address"}
                  value={borrower?.emailAddress || "Unknown"}
                  icon={
                    <MailOutline sx={{ fontSize: "12px" }} className="thin" />
                  }
                />
                <DisplayPreviewItem
                  label={"Phone Number"}
                  value={formatPhone(borrower?.phone) || "Unknown"}
                  icon={
                    <PhoneOutlined sx={{ fontSize: "12px" }} className="thin" />
                  }
                />
                <DisplayPreviewItem
                  label={"Date of Birth"}
                  value={formatDate(borrower?.dateOfBirth) || "Unknown"}
                  icon={
                    <CalendarMonth sx={{ fontSize: "12px" }} className="thin" />
                  }
                />
              </Flx>
            )} */}
          </Flx>
        </Flx>

        <Flx sx={{ mt: 2.5 }} column g={0}>
          <BorrowerSummaryCardSection
            isActive={activeBorrowerId}
            label="Required Document Checklist"
          >
            <BorrowerRequiredDocumentsChecklist borrower={borrower} />
          </BorrowerSummaryCardSection>
          <Divider />
          <BorrowerSummaryCardSection
            isActive={activeBorrowerId}
            label="All Borrower Documents"
            defaultOpen={false}
            // label="All Associated Documents"
            // label="All Associated Borrower Documents"
          >
            <BorrowerAssociatedFilesPreview borrower={borrower} />
          </BorrowerSummaryCardSection>
        </Flx>

        <Flx sx={{ mt: 3 }} gap={1}>
          {currentBorrowerActive ? null : (
            <>
              <SendBorrowerAppLinkButton
                icon={<TextSnippetOutlined />}
                type={"loanApp"}
                borrower={borrower}
              >
                Send Loan App
              </SendBorrowerAppLinkButton>
              <SendBorrowerAppLinkButton
                type={"creditAuth"}
                icon={<CreditScoreOutlined />}
                borrower={borrower}
              >
                Send Credit Auth
              </SendBorrowerAppLinkButton>
              <AssignLoanFilesButton borrower={borrower}>
                Assign Files To Borrower
              </AssignLoanFilesButton>
            </>
          )}
          <Button
            onClick={() => onBorrowerClick(borrower?._id)}
            // fullWidth
            variant="contained"
            // variant="text"
            size="large"
            sx={{
              flexGrow: 1,
              // background:
              //   borrower?._id === activeBorrowerId ? blue[100] : blue[50],
            }}
            endIcon={
              currentBorrowerActive ? null : ( // <CloseOutlined />
                <EastOutlined />
              )
            }
          >
            {currentBorrowerActive
              ? "Close Borrower Details"
              : "View Borrower Details"}
          </Button>
        </Flx>
      </Flx>
    </Card>
  );
};

const BorrowerSummaryCardSection = ({
  label,
  children,
  isActive,
  defaultOpen,
}) => {
  const [show, setShow] = useState(defaultOpen ?? null);

  useEffect(() => {
    // setShow(false);
    // if (isActive) {
    //   setShow(true);
    // } else {
    //   setShow(false);
    // }
    if (isNil(show)) {
      setShow(true);
      return;
    }
    setShow(false);
    // if (isActive) {
    //   setShow(false);
    // } else {
    //   setShow(true);
    // }
  }, [isActive]);
  return (
    <Box
      onClick={() => (show ? null : setShow(!show))}
      sx={{
        // py: 1.5,
        // px: 1,
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        py: 1.5,
        px: 1,
        // gap: 1,
        "&:hover": {
          // backgroundColor: "rgba(0,0,0,0.02)",
          backgroundColor: !show ? "rgba(0,0,0,0.02)" : "transparent",
        },
      }}
    >
      <Box
        onClick={() => (show ? setShow(!show) : null)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // cursor: "pointer",
          // mb: 1,
          "&:hover": {
            // backgroundColor: "rgba(0,0,0,0.02)",
            backgroundColor: show ? "rgba(0,0,0,0.02)" : "transparent",
          },
        }}
      >
        <SectionLabel>{label}</SectionLabel>
        {show ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
      </Box>
      {show ? children : null}
    </Box>
  );
};

const DisplayPreviewItem = ({ label, icon, value, sx, tooltip }) => {
  return (
    <Tooltip title={tooltip || label} arrow placement="right">
      <Box
        sx={{
          width: "max-content",
          // py: 0.5,
          // px: 0.5,
          //
          fontWeight: 400,
        }}
      >
        {icon ? (
          <Flx ac g={0.4} wrap>
            {icon}
            <Txt secondary>{value}</Txt>
          </Flx>
        ) : (
          <Txt secondary>{value}</Txt>
        )}
      </Box>
    </Tooltip>
  );
};

const BorrowerAssociatedFilesPreview = ({ borrower }) => {
  const { getAssociationDocumentsByBorrowerId } = useAssociationUtils();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const borrowerFiles = useMemo(() => {
    return getAssociationDocumentsByBorrowerId(loanDrilldown, borrower?._id);
  }, [loanDrilldown, borrower]);

  if (isEmpty(borrowerFiles)) {
    return (
      <Txt
        sx={{
          // fontStyle: "italic",
          //
          opacity: 0.85,
        }}
        secondary
      >
        No files associated with borrower.
      </Txt>
    );
  }
  return (
    <Flx gap={0.5} wrap>
      {borrowerFiles.map((file) => (
        <PreviewableLoanFileButton key={file._id} file={file} />
      ))}
    </Flx>
  );
};

/**
 * UI COMPONETS
 */

const SectionLabel = ({ children }) => {
  const fontSize = "13px";
  // const fontSize = "12px";
  const fontWeight = 400;
  // const fontWeight = 500;
  if (isNil(children)) {
    return null;
  }
  return <Txt sx={{ fontSize, fontWeight, mb: 0.5 }}>{children}</Txt>;
};

export default BorrowerSummaryCardItem;
